import { motion } from "framer-motion";
import { Sparkles, Hammer, Gem, Leaf } from "lucide-react";

const PILLARS = [
  {
    icon: Sparkles,
    title: "Innovation",
    body: "Asymmetric silhouettes, denim reimagined as couture, jewellery cast in motion.",
  },
  {
    icon: Hammer,
    title: "Craftsmanship",
    body: "Hand-finished in our Milano atelier by seven master makers.",
  },
  {
    icon: Gem,
    title: "Exclusivity",
    body: "Editions of fewer than fifty. Numbered, signed, never repeated.",
  },
  {
    icon: Leaf,
    title: "Sustainability",
    body: "Single-source materials, slow production, lifelong repair guarantee.",
  },
];

export default function WhyAsymmetry() {
  return (
    <section
      id="why"
      data-testid="why-asymmetry"
      className="relative bg-midnight-deep py-24 md:py-36 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      {/* Soft glow */}
      <div
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 -left-40 w-[480px] h-[480px] rounded-full bg-denim/10 blur-3xl pointer-events-none"
      />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
        <div className="md:col-span-5">
          <div className="eyebrow mb-6">Why Asymmetry</div>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9 }}
            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight"
          >
            Four <span className="italic text-silver">tenets</span>.
            <br />
            One house.
          </motion.h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 flex items-end">
          <p className="text-white/60 text-base md:text-lg font-light leading-relaxed max-w-md">
            We do not chase seasons. We chase silhouettes that outlast them.
            These are the principles that shape every Asymmetry Edit.
          </p>
        </div>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              data-testid={`pillar-${p.title.toLowerCase()}`}
              className="group bg-midnight-deep p-8 md:p-10 lg:p-12 min-h-[340px] flex flex-col justify-between hover:bg-midnight-panel transition-colors duration-700"
            >
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-silver/60 mb-8">
                  0{i + 1}
                </div>
                <Icon
                  size={28}
                  strokeWidth={1.1}
                  className="text-silver group-hover:text-white transition-colors duration-700"
                />
                <h3 className="font-serif text-white text-3xl md:text-4xl mt-8 leading-tight">
                  {p.title}
                </h3>
              </div>
              <p className="text-white/55 text-sm md:text-base font-light leading-relaxed mt-8">
                {p.body}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
